import { inject, Injectable } from '@angular/core';
import { PriceListNoteApiDto } from '@lx/shared/api';
import { PricelistControllerApiService, PricelistPrivateControllerApiService } from '@lx/shared/api/services';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';
import { catchError, combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { AcademicYearData, JuniorPriceLists, JuniorPriceListsFromApi, PriceList, PriceListData, YoungAdultsPriceListsFromApi } from '../models';
import { Company, CompanyEnum } from '../models/company.model';
import { PriceListArea } from '../models/pricelist-area.model';
import { PriceListOnlineWithDisclaimers } from '../models/pricelist-online-courses.model';
import { PriceListPublicHolidays } from '../models/pricelist-public-holiday.model';
import { PriceListTermsAndConditions } from '../models/pricelist-terms-and-conditions';
import { PriceListTermsLanguage } from '../models/pricelist-terms-language.mode';
import SubjectEnum = PriceListNoteApiDto.SubjectEnum;

@Injectable({
  providedIn: 'root',
})
export class PriceListService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly priceListService = inject(PricelistControllerApiService);
  private readonly priceListPrivateService = inject(PricelistPrivateControllerApiService);
  private readonly DEFAULT_SELECTED_LANGUAGE = 'en-US';

  fetchAllData(priceListArea: PriceListArea, year: number, privateAccess: boolean): Observable<PriceListData> {
    return combineLatest([
      this.getPriceList(priceListArea, year, privateAccess),
      this.getJuniorCampsPriceList(priceListArea, year, privateAccess),
      this.getYoungAdultsPriceLists(priceListArea, year, privateAccess),
      this.getAyaPriceList(priceListArea, year, privateAccess).pipe(catchError(() => of(null))),
      this.getOnlineCourses(priceListArea, year, privateAccess).pipe(catchError(() => of(null))),
      this.getPublicHolidays(priceListArea, year),
      this.getTermsAndConditionsLanguages(priceListArea),
      this.getTermsAndConditions(CompanyEnum.KIL, this.DEFAULT_SELECTED_LANGUAGE),
      this.getTermsAndConditions(CompanyEnum.ALP, this.DEFAULT_SELECTED_LANGUAGE),
    ]).pipe(
      map(
        ([
          priceLists,
          juniorCampsPriceLists,
          youngAdultsPriceLists,
          ayaPriceLists,
          onlineCourses,
          publicHolidays,
          termsAndConditionsLanguages,
          termsAndConditionsKIL,
          termsAndConditionsALP,
        ]) =>
          ({
            priceLists,
            juniorCampsPriceLists,
            youngAdultsPriceLists,
            ayaPriceLists,
            onlineCourses,
            publicHolidays,
            termsAndConditionsLanguages,
            priceListArea,
            termsAndConditionsKIL,
            termsAndConditionsALP,
          } as PriceListData),
      ),
    );
  }

  getPriceList(area: PriceListArea, priceListYear: number, privateAccess: boolean): Observable<Record<Company, PriceList[]>> {
    let priceLists$: Observable<PriceList[]>;
    if (!privateAccess) {
      priceLists$ = this.getPublicPriceList(area, priceListYear);
    } else {
      priceLists$ = this.getPrivatePriceList(area, priceListYear);
    }

    return priceLists$.pipe(
      map((priceLists: PriceList[]) => {
        return priceLists.reduce((acc: Record<Company, PriceList[]>, priceList: PriceList) => {
          const company = priceList.company;
          if (!acc[company]) {
            acc[company] = [];
          }
          acc[company].push(priceList);
          return acc;
        }, {} as Record<Company, PriceList[]>);
      }),
    );
  }

  getPublicPriceList(area: PriceListArea, year: number): Observable<PriceList[]> {
    return this.priceListService
      .getSchoolPriceLists(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      )
      .pipe(switchMap((res) => (res ? of(res) : of([]))));
  }

  getPrivatePriceList(area: PriceListArea, year: number): Observable<PriceList[]> {
    return this.priceListPrivateService
      .getPrivateSchoolPriceLists({ area, year }, this.locale, this.companyHeader)
      .pipe(switchMap((res) => (res ? of(res) : of([]))));
  }

  getTermsAndConditions(company: Company, language = 'en-US'): Observable<PriceListTermsAndConditions> {
    return this.priceListService.getPriceListsTermsAndConditions(language, company);
  }

  getTermsAndConditionsLanguages(area: PriceListArea): Observable<PriceListTermsLanguage[]> {
    return this.priceListService.getPriceListsTermsAndConditionsLanguages(this.locale, this.companyHeader, area);
  }

  getPublicHolidays(area: PriceListArea, year: number): Observable<PriceListPublicHolidays> {
    return this.priceListService.getPriceListsPublicHolidaysV2(
      {
        area,
        year,
      },
      this.locale,
      this.companyHeader,
    );
  }

  getOnlineCourses(area: PriceListArea, year: number, privateAccess: boolean): Observable<PriceListOnlineWithDisclaimers> {
    let onlinePriceLists$: Observable<PriceListOnlineWithDisclaimers>;
    if (!privateAccess) {
      onlinePriceLists$ = this.priceListService.getOnlinePriceListsWithDisclaimers(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      );
    } else {
      onlinePriceLists$ = this.priceListPrivateService.getPrivateOnlinePriceListsV2(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      );
    }
    return onlinePriceLists$;
  }

  getJuniorCampsPriceList(area: PriceListArea, year: number, privateAccess: boolean): Observable<JuniorPriceLists> {
    let juniorCampsPriceLists$: Observable<JuniorPriceListsFromApi>;
    if (!privateAccess) {
      juniorCampsPriceLists$ = this.priceListService.getJuniorCampsPriceLists(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      );
    } else {
      juniorCampsPriceLists$ = this.priceListPrivateService.getPrivateJuniorCampsPriceLists(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      );
    }

    return juniorCampsPriceLists$.pipe(
      map(this.convertPrivateLessonsAndNotes),
      map((priceLists: JuniorPriceLists) => {
        const sortedCamps = priceLists.camps?.sort(
          (campA, campB) => campA.weight - campB.weight || (campA.juniorCampInfo?.country?.name ?? '').localeCompare(campB.juniorCampInfo?.country?.name ?? ''),
        );
        return {
          camps: sortedCamps || [],
        };
      }),
    );
  }

  getYoungAdultsPriceLists(area: PriceListArea, year: number, privateAccess: boolean): Observable<YoungAdultsPriceListsFromApi> {
    if (privateAccess) {
      return this.priceListPrivateService.getPrivateYoungAdultsPriceLists(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      );
    }

    return this.priceListService.getYoungAdultsPriceLists(
      {
        area,
        year,
      },
      this.locale,
      this.companyHeader,
    );
  }

  getAyaPriceList(area: PriceListArea, year: number, privateAccess: boolean): Observable<AcademicYearData> {
    let ayaPriceLists$: Observable<AcademicYearData>;
    if (!privateAccess) {
      ayaPriceLists$ = this.priceListService.getAyaPriceLists(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      );
    } else {
      ayaPriceLists$ = this.priceListPrivateService.getPrivateAyaPriceLists(
        {
          area,
          year,
        },
        this.locale,
        this.companyHeader,
      );
    }
    return ayaPriceLists$;
  }

  private convertPrivateLessonsAndNotes(juniorCamps: JuniorPriceListsFromApi): JuniorPriceLists {
    return {
      ...juniorCamps,
      camps: juniorCamps.camps.map((camp) => ({
        ...camp,
        privateLessons: camp?.privateLessons?.map(({ lessons }) => ({
          name: `+${lessons[0].numberOfLessons} lessons (${lessons[0].minutesPerLesson} minutes per lesson)`,
          price: lessons[0].price,
        })),
        additionalInfo: camp?.notes
          ?.filter((note) => note?.subject === SubjectEnum.ADDITIONAL_INFORMATION)
          .map(({ text, ...otherFields }) => ({
            ...otherFields,
            note: text,
          })),
        notes: camp?.notes?.filter((note) => note?.subject !== SubjectEnum.ADDITIONAL_INFORMATION),
      })),
    };
  }
}
